import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Home as HomeIcon, MapPin, Ruler, Sparkles, Camera, FileLock, Eye, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError, resolveMediaUrl } from "@/lib/api";
import MediaUploader from "@/components/MediaUploader";
import SpecializedListingFields from "@/components/SpecializedListingFields";
import { REAL_ESTATE_CATEGORIES } from "@/lib/catalog";

const CATEGORIES = Object.entries(REAL_ESTATE_CATEGORIES).map(([value, label]) => ({ value, label }));

const EQUIPMENT_BASE = [
  "Sécurité 24/7", "Clôturé", "Portail motorisé", "Climatisation",
  "Générateur", "Panneaux solaires", "Forage / eau", "Citerne",
  "Fibre optique", "Cuisine équipée", "Meublé", "Ascenseur",
  "Parking couvert", "Piscine", "Jardin", "Terrasse", "Balcon",
  "Vue dégagée", "Domotique", "Buanderie", "Wi-Fi haut débit",
  "Vidéoprojecteur", "Sonorisation", "Scène", "Chaises & tables", "Service traiteur",
];

const STATUSES = [
  { value: "available", label: "Disponible", tone: "bg-emerald-50 text-emerald-700" },
  { value: "negotiation", label: "En négociation", tone: "bg-amber-50 text-amber-700" },
  { value: "pending", label: "Réservé", tone: "bg-blue-50 text-blue-700" },
  { value: "sold", label: "Vendu", tone: "bg-slate-100 text-slate-700" },
  { value: "rented", label: "Loué", tone: "bg-slate-100 text-slate-700" },
  { value: "inactive", label: "Désactivé", tone: "bg-red-50 text-red-700" },
];

const empty = {
  sector: "real_estate",
  purpose: "sale",
  category: "villa",
  title: "",
  title_en: "",
  description: "",
  description_en: "",
  price: "",
  currency: "USD",
  price_period: "",
  negotiable: false,
  country: "RDC",
  city: "Kinshasa",
  zone: "",
  address: "",
  specs: {
    chambres: "",
    sdb: "",
    surface_habitable_m2: "",
    surface_terrain_m2: "",
    etage: "",
    etages_total: "",
    annee_construction: "",
    orientation: "",
    etat: "",
    titre_foncier: false,
    charges_mensuelles: "",
    avance_mois: "",
    commission_mois: "",
    caution_mois: "",
    duree_bail: "",
    ameublement: "non_meuble",
    type_location: "longue_duree",
    capacite_personnes: "",
    salles_reunion: "",
    duree_incluse_heures: "",
    caution_evenement: "",
    horaires_ouverture: "",
    cuisine: "",
    salons: "",
    toilettes: "",
    parking_places: "",
    acces_pmr: false,
    disponibilite: "",
    conditions_location: "",
  },
  equipment: [],
  images: [],
  videos: [],
  documents: [],
  private_documents: [],
  private_data: {},
  publication_stage: "published",
  status: "available",
  featured: false,
};

const STEPS = [
  { key: "type", label: "Type & objet", icon: HomeIcon },
  { key: "location", label: "Localisation", icon: MapPin },
  { key: "specs", label: "Caractéristiques", icon: Ruler },
  { key: "equipment", label: "Équipements", icon: Sparkles },
  { key: "description", label: "Description & prix", icon: FileLock },
  { key: "media", label: "Médias", icon: Camera },
  { key: "documents", label: "Documents privés", icon: FileLock },
  { key: "publish", label: "Publication", icon: Eye },
];

export default function WizardImmo() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const editing = Boolean(id);
  const [searchParams] = useSearchParams();
  const initialForm = { ...empty, purpose: searchParams.get("purpose") || empty.purpose, category: searchParams.get("category") || empty.category };
  const [step, setStep] = useState(editing ? 0 : (searchParams.has("purpose") || searchParams.has("category") ? 1 : 0));
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (editing || !location.state?.quickDraft) return;
    const draft = location.state.quickDraft;
    setForm((current) => ({ ...current, ...draft, specs: { ...current.specs, ...(draft.specs || {}) } }));
  }, [editing, location.state]);

  useEffect(() => {
    if (!editing) return;
    setLoading(true);
    setLoadError("");
    api
      .get(`/admin/listings/${id}`)
      .then((r) => {
        setForm({ ...empty, ...r.data, specs: { ...empty.specs, ...(r.data.specs || {}) } });
        setStep(4);
      })
      .catch((e) => {
        const message = formatApiError(e.response?.data?.detail) || "Impossible de charger cette annonce.";
        setLoadError(message);
        toast.error(message);
      })
      .finally(() => setLoading(false));
  }, [editing, id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateSpec = (k, v) => setForm((f) => ({ ...f, specs: { ...f.specs, [k]: v } }));
  const toggleEquip = (name) =>
    setForm((f) => ({
      ...f,
      equipment: f.equipment.includes(name) ? f.equipment.filter((x) => x !== name) : [...f.equipment, name],
    }));

  const isLand = form.category === "terrain";
  const isBuilding = ["immeuble", "local", "boutique", "bureau", "cabinet", "atelier", "entrepot", "restaurant"].includes(form.category);
  const isSharedOrEvent = ["coworking", "bureau_prive", "salle_reunion", "salle_conference", "salle_formation", "centre_formation", "salle_mariage", "salle_reception", "espace_exterieur"].includes(form.category);
  const isEvent = ["salle_conference", "salle_formation", "centre_formation", "salle_mariage", "salle_reception", "espace_exterieur"].includes(form.category);
  const isRent = form.purpose === "rent";

  const canProceed = useMemo(() => {
    if (step === 0) return form.category && form.purpose;
    if (step === 1) return form.city && form.zone;
    if (step === 4) return form.title.trim() && Number(form.price) > 0;
    return true;
  }, [step, form]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        specs: Object.fromEntries(
          Object.entries(form.specs).map(([k, v]) => [k, typeof v === "string" && v !== "" && !isNaN(v) ? Number(v) : v])
        ),
      };
      if (editing) await api.put(`/listings/${id}`, payload);
      else await api.post("/listings", payload);
      toast.success(editing ? "Annonce mise à jour" : "Annonce créée");
      nav("/admin/listings");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Chargement…</div>;
  if (editing && loadError) return <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700"><div className="font-semibold">Annonce non chargée</div><p className="mt-1 text-sm">{loadError}</p><Link to="/admin/listings" className="mt-4 inline-flex text-sm font-semibold underline">Retour aux annonces</Link></div>;

  return (
    <div className="space-y-6" data-testid="wizard-immo">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-brand-orange font-semibold">{editing ? "Édition de l'annonce" : "Wizard Immobilier"}</div>
          <h1 className="mt-1 font-display text-3xl lg:text-4xl text-brand-navy tracking-tight">
            {editing ? "Modifier une annonce" : "Nouvelle annonce immobilière"}
          </h1>
        </div>
        <Link to="/admin/listings" className="text-sm text-slate-500 hover:text-brand-navy inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Retour à l&apos;inventaire
        </Link>
      </div>

      {editing && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <div><div className="text-sm font-bold text-brand-navy">Vous modifiez : {form.title}</div><div className="text-xs text-slate-600">Changez le prix ou n'importe quel champ, puis enregistrez.</div></div>
          <Button type="button" onClick={submit} disabled={saving || !form.title.trim() || Number(form.price) <= 0} className="bg-brand-orange hover:bg-brand-orangeDark text-white" data-testid="edit-save-top"><Check className="h-4 w-4 mr-1" />{saving ? "Enregistrement…" : "Enregistrer les modifications"}</Button>
        </div>
      )}

      {/* Stepper */}
      <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
        <ol className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => (editing || done || active ? setStep(i) : null)}
                  className={`w-full flex items-center gap-2 rounded-lg px-2.5 py-2 border text-left transition-colors ${
                    active
                      ? "border-brand-orange bg-brand-orange/10 text-brand-navy"
                      : done
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-slate-200 bg-white text-slate-500"
                  }`}
                  data-testid={`step-${s.key}`}
                >
                  <span
                    className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${
                      active ? "bg-brand-orange text-white" : done ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"
                    }`}
                  >
                    {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                  </span>
                  <span className="hidden md:inline text-[11px] font-semibold truncate">{s.label}</span>
                  <Icon className="h-3.5 w-3.5 md:hidden" />
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-6 lg:p-8 shadow-sm space-y-6">
        {step === 0 && (
          <div className="space-y-6">
            <h2 className="font-display text-xl text-brand-navy">Que voulez-vous faire ?</h2>
            <div className="grid grid-cols-2 gap-3">
              {["sale", "rent"].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => update("purpose", p)}
                  className={`rounded-xl border-2 p-4 text-left transition-colors ${
                    form.purpose === p ? "border-brand-orange bg-brand-orange/5" : "border-slate-200 hover:border-brand-orange/60"
                  }`}
                  data-testid={`purpose-${p}`}
                >
                  <div className="text-lg font-bold text-brand-navy">{p === "sale" ? "Vendre" : "Louer"}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {p === "sale" ? "Mise en vente avec transfert de propriété." : "Mise en location courte ou longue durée."}
                  </div>
                </button>
              ))}
            </div>

            <div>
              <Label className="text-sm font-semibold text-brand-navy">Type de bien</Label>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => update("category", c.value)}
                    className={`rounded-lg border px-3 py-2 text-sm text-left transition-colors ${
                      form.category === c.value
                        ? "border-brand-orange bg-brand-orange text-white font-semibold"
                        : "border-slate-200 hover:border-brand-orange/60"
                    }`}
                    data-testid={`category-${c.value}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5">
            <h2 className="font-display text-xl text-brand-navy">Où se situe le bien ?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Pays</Label>
                <Input value={form.country} onChange={(e) => update("country", e.target.value)} data-testid="field-country" />
              </div>
              <div>
                <Label>Ville *</Label>
                <Input value={form.city} onChange={(e) => update("city", e.target.value)} required data-testid="field-city" />
              </div>
              <div>
                <Label>Quartier / Zone *</Label>
                <Input value={form.zone} onChange={(e) => update("zone", e.target.value)} placeholder="Ex : Golf, Bel Air, Ruashi" required data-testid="field-zone" />
              </div>
              <div className="md:col-span-3">
                <Label>Adresse précise (interne, non publiée)</Label>
                <Input value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="Numéro, avenue, repère…" data-testid="field-address" />
                <p className="mt-1 text-xs text-slate-500">L&apos;adresse exacte reste privée. Sur le site public, seul le quartier est affiché.</p>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <h2 className="font-display text-xl text-brand-navy">Caractéristiques principales</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {!isLand && (
                <>
                  <Field label="Chambres" v={form.specs.chambres} onChange={(v) => updateSpec("chambres", v)} type="number" />
                  <Field label="Salles de bain" v={form.specs.sdb} onChange={(v) => updateSpec("sdb", v)} type="number" />
                  <Field label="Surface habitable (m²)" v={form.specs.surface_habitable_m2} onChange={(v) => updateSpec("surface_habitable_m2", v)} type="number" />
                </>
              )}
              <Field label={isLand ? "Surface terrain (m²) *" : "Surface terrain (m²)"} v={form.specs.surface_terrain_m2} onChange={(v) => updateSpec("surface_terrain_m2", v)} type="number" />
              {!isLand && (
                <>
                  <Field label="Étage" v={form.specs.etage} onChange={(v) => updateSpec("etage", v)} type="number" />
                  {isBuilding && (
                    <Field label="Nombre d'étages" v={form.specs.etages_total} onChange={(v) => updateSpec("etages_total", v)} type="number" />
                  )}
                  <Field label="Année de construction" v={form.specs.annee_construction} onChange={(v) => updateSpec("annee_construction", v)} type="number" />
                </>
              )}
              {isLand && (
                <div className="col-span-2 md:col-span-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!form.specs.titre_foncier}
                      onChange={(e) => updateSpec("titre_foncier", e.target.checked)}
                      data-testid="field-titre-foncier"
                    />
                    <span>Titre foncier disponible</span>
                  </label>
                </div>
              )}
              <div className="col-span-2">
                <Label>État général</Label>
                <Select value={form.specs.etat || ""} onValueChange={(v) => updateSpec("etat", v)}>
                  <SelectTrigger data-testid="field-etat"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="neuf">Neuf</SelectItem>
                    <SelectItem value="excellent">Excellent</SelectItem>
                    <SelectItem value="bon">Bon</SelectItem>
                    <SelectItem value="a_renover">À rénover</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Orientation</Label>
                <Select value={form.specs.orientation || ""} onValueChange={(v) => updateSpec("orientation", v)}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nord">Nord</SelectItem>
                    <SelectItem value="sud">Sud</SelectItem>
                    <SelectItem value="est">Est</SelectItem>
                    <SelectItem value="ouest">Ouest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isRent && (
                <>
                  <div><Label>Ameublement</Label><Select value={form.specs.ameublement || "non_meuble"} onValueChange={(v) => updateSpec("ameublement", v)}><SelectTrigger data-testid="field-ameublement"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="meuble">Meublé</SelectItem><SelectItem value="semi_meuble">Semi-meublé</SelectItem><SelectItem value="non_meuble">Non meublé</SelectItem></SelectContent></Select></div>
                  <div><Label>Offre de location</Label><Select value={form.specs.type_location || "longue_duree"} onValueChange={(v) => updateSpec("type_location", v)}><SelectTrigger data-testid="field-rental-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="courte_duree">Courte durée</SelectItem><SelectItem value="mensuelle">Mensuelle</SelectItem><SelectItem value="longue_duree">Longue durée</SelectItem></SelectContent></Select></div>
                  <Field label="Charges mensuelles (USD)" v={form.specs.charges_mensuelles} onChange={(v) => updateSpec("charges_mensuelles", v)} type="number" />
                  <Field label="Avance de loyer (mois)" v={form.specs.avance_mois} onChange={(v) => updateSpec("avance_mois", v)} type="number" />
                  <Field label="Commission Team Work (mois)" v={form.specs.commission_mois} onChange={(v) => updateSpec("commission_mois", v)} type="number" />
                  <Field label="Caution (mois)" v={form.specs.caution_mois} onChange={(v) => updateSpec("caution_mois", v)} type="number" />
                  <Field label="Durée bail (mois)" v={form.specs.duree_bail} onChange={(v) => updateSpec("duree_bail", v)} type="number" />
                </>
              )}
              {isSharedOrEvent && <><Field label="Capacité (personnes)" v={form.specs.capacite_personnes} onChange={(v) => updateSpec("capacite_personnes", v)} type="number" /><Field label="Salles de réunion / espaces" v={form.specs.salles_reunion} onChange={(v) => updateSpec("salles_reunion", v)} type="number" /><Field label="Durée incluse (heures)" v={form.specs.duree_incluse_heures} onChange={(v) => updateSpec("duree_incluse_heures", v)} type="number" /><Field label="Caution événement (USD)" v={form.specs.caution_evenement} onChange={(v) => updateSpec("caution_evenement", v)} type="number" /><Field label="Horaires d'ouverture" v={form.specs.horaires_ouverture} onChange={(v) => updateSpec("horaires_ouverture", v)} /></>}
              {isSharedOrEvent && <><Field label="Toilettes" v={form.specs.toilettes} onChange={(v) => updateSpec("toilettes", v)} type="number" /><Field label="Places de parking" v={form.specs.parking_places} onChange={(v) => updateSpec("parking_places", v)} type="number" /><Field label="Disponibilité" v={form.specs.disponibilite} onChange={(v) => updateSpec("disponibilite", v)} /><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.specs.acces_pmr} onChange={(e) => updateSpec("acces_pmr", e.target.checked)} /><span>Accès PMR</span></label></>}
              {isEvent && <><Field label="Nombre de salons / espaces" v={form.specs.salons} onChange={(v) => updateSpec("salons", v)} type="number" /><Field label="Cuisine / espace traiteur" v={form.specs.cuisine} onChange={(v) => updateSpec("cuisine", v)} /><div className="col-span-2"><Field label="Conditions de location" v={form.specs.conditions_location} onChange={(v) => updateSpec("conditions_location", v)} /></div></>}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl text-brand-navy">Équipements & options</h2>
            <p className="text-sm text-slate-500">Cochez tout ce qui correspond au bien. Ces éléments apparaîtront dans l&apos;annonce publique.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {EQUIPMENT_BASE.map((eq) => {
                const on = form.equipment.includes(eq);
                return (
                  <button
                    key={eq}
                    type="button"
                    onClick={() => toggleEquip(eq)}
                    className={`text-left rounded-lg border px-3 py-2 text-sm transition-colors ${
                      on ? "border-brand-orange bg-brand-orange/10 text-brand-navy font-semibold" : "border-slate-200 hover:border-brand-orange/50"
                    }`}
                    data-testid={`equip-${eq}`}
                  >
                    {on ? "✓ " : ""}
                    {eq}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-5">
            <h2 className="font-display text-xl text-brand-navy">Description & prix</h2>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Titre (FR) *</Label>
                <Input value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Ex : Villa contemporaine à piscine — Golf" required data-testid="field-title" />
              </div>
              <div>
                <Label>Title (EN)</Label>
                <Input value={form.title_en} onChange={(e) => update("title_en", e.target.value)} placeholder="Ex : Modern villa with pool — Golf" />
              </div>
              <div>
                <Label>Description (FR)</Label>
                <Textarea rows={4} value={form.description} onChange={(e) => update("description", e.target.value)} data-testid="field-description" />
              </div>
              <div>
                <Label>Description (EN)</Label>
                <Textarea rows={4} value={form.description_en} onChange={(e) => update("description_en", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label>Prix *</Label>
                  <Input type="number" value={form.price} onChange={(e) => update("price", e.target.value)} required data-testid="field-price" />
                </div>
                <div>
                  <Label>Devise</Label>
                  <Select value={form.currency} onValueChange={(v) => update("currency", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="CDF">CDF</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isRent && (
                  <div>
                    <Label>Périodicité</Label>
                    <Select value={form.price_period || "month"} onValueChange={(v) => update("price_period", v)}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="month">/ mois</SelectItem>
                        <SelectItem value="day">/ jour</SelectItem>
                        <SelectItem value="hour">/ heure</SelectItem>
                        <SelectItem value="event">/ événement</SelectItem>
                        <SelectItem value="week">/ semaine</SelectItem>
                        <SelectItem value="year">/ an</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <label className="flex items-center gap-2 self-end pb-2 text-sm">
                  <input type="checkbox" checked={form.negotiable} onChange={(e) => update("negotiable", e.target.checked)} data-testid="field-negotiable" />
                  Prix négociable
                </label>
              </div>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-6">
            <h2 className="font-display text-xl text-brand-navy">Photos & vidéos</h2>
            <MediaUploader
              value={form.images}
              onChange={(v) => update("images", v)}
              accept="image"
              label="Photos (la 1ère devient l'image de couverture)"
              maxItems={30}
            />
            <MediaUploader
              value={form.videos}
              onChange={(v) => update("videos", v)}
              accept="video"
              label="Vidéos (visite virtuelle, drone, présentation)"
              maxItems={5}
            />
          </div>
        )}

        {step === 6 && (
          <div className="space-y-6">
            <h2 className="font-display text-xl text-brand-navy">Documents</h2>
            <MediaUploader
              value={form.documents}
              onChange={(v) => update("documents", v)}
              accept="document"
              label="Documents publics (brochure, plan, notice)"
              maxItems={10}
            />
            <div className="rounded-xl border border-red-100 bg-red-50/40 p-4">
              <div className="flex items-center gap-2 text-red-700 font-semibold text-sm">
                <FileLock className="h-4 w-4" /> Documents privés — Espace Owner uniquement
              </div>
              <p className="mt-1 text-xs text-red-700/80">
                Ces fichiers sont chiffrés côté stockage et jamais exposés publiquement.
                Ils ne sont visibles qu&apos;après authentification Owner.
              </p>
              <div className="mt-3">
                <MediaUploader
                  value={form.private_documents}
                  onChange={(v) => update("private_documents", v)}
                  accept="document"
                  isPrivate
                  label="Titre foncier, contrat, pièce d'identité vendeur, etc."
                  maxItems={20}
                />
              </div>
            </div>
          </div>
        )}

        {step === 7 && (
          <div className="space-y-6">
            <h2 className="font-display text-xl text-brand-navy">Publication</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <Label>Statut de publication</Label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {STATUSES.map((s) => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => update("status", s.value)}
                        className={`rounded-lg border px-3 py-2 text-sm ${
                          form.status === s.value ? "border-brand-orange bg-brand-orange text-white font-semibold" : "border-slate-200 hover:border-brand-orange/50"
                        }`}
                        data-testid={`status-${s.value}`}
                      >
                        <span className={form.status === s.value ? "text-white" : ""}>{s.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-3 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => update("featured", e.target.checked)} data-testid="field-featured" />
                  <Star className="h-4 w-4 text-brand-orange" />
                  <span className="text-sm">Mettre en vedette (Featured)</span>
                </label>
              </div>

              <aside className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Aperçu</div>
                <div className="mt-3 rounded-lg overflow-hidden bg-white border border-slate-200">
                  {form.images[0] ? (
                    <img src={resolveMediaUrl(form.images[0])} alt="" className="w-full h-40 object-cover" />
                  ) : (
                    <div className="w-full h-40 bg-slate-200 flex items-center justify-center text-slate-500 text-xs">Pas de photo</div>
                  )}
                  <div className="p-3 space-y-1">
                    <div className="text-[10px] uppercase tracking-widest text-brand-orange font-bold">
                      {form.purpose === "sale" ? "À vendre" : "À louer"} · {form.category}
                    </div>
                    <div className="text-sm font-bold text-brand-navy truncate">{form.title || "Titre de l'annonce"}</div>
                    <div className="text-xs text-slate-500">{form.zone && `${form.zone} · `}{form.city}</div>
                    <div className="text-sm font-bold text-brand-navy pt-1">
                      {form.price ? `${Number(form.price).toLocaleString()} ${form.currency}` : "—"}
                      {isRent && form.price_period ? ` /${form.price_period}` : ""}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-[11px] text-slate-500">
                  {form.images.length} photo(s) · {form.videos.length} vidéo(s) · {form.documents.length} doc public(s) · {form.private_documents.length} doc privé(s)
                </div>
              </aside>
            </div>
          </div>
        )}
      </div>

      {step === STEPS.length - 1 && (<SpecializedListingFields sector={form.sector} purpose={form.purpose} category={form.category} price={form.price} specs={form.specs} privateData={form.private_data || {}} publicationStage={form.publication_stage || "published"} onSpecChange={updateSpec} onPrivateChange={(key, value) => setForm((current) => ({ ...current, private_data: { ...(current.private_data || {}), [key]: value } }))} onPublicationStage={(value) => update("publication_stage", value)} />)}


      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          data-testid="wizard-prev"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Précédent
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            type="button"
            onClick={() => setStep((s) => s + 1)}
            disabled={!canProceed}
            className="bg-brand-navy hover:bg-brand-navy/90 text-white"
            data-testid="wizard-next"
          >
            Suivant <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button
            type="button"
            onClick={submit}
            disabled={saving || !form.title.trim() || Number(form.price) <= 0}
            className="bg-brand-orange hover:bg-brand-orangeDark text-white"
            data-testid="wizard-submit"
          >
            <Check className="h-4 w-4 mr-1" /> {editing ? "Enregistrer les modifications" : "Publier l'annonce"}
          </Button>
        )}
      </div>
    </div>
  );
}

function Field({ label, v, onChange, type = "text" }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={v ?? ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
